"use client";

/**
 * Root-mounted host for the post-purchase confirmation modal. Subscribes to the
 * imperative store and renders the modal whenever a confirmation is active, so
 * any purchase site anywhere in the app can trigger the prominent confirmation
 * with a single `showPurchaseConfirmation(...)` call. Lives next to <Toaster />
 * in app/layout.tsx.
 *
 * On mount it also replays any confirmation queued in sessionStorage — the
 * hand-off used by purchases that complete WITHOUT a Stripe redirect (e.g. an
 * in-app upgrade charged to a saved card) and then do a full-page reload.
 */
import { useEffect } from "react";
import {
  consumeQueuedPurchaseConfirmation,
  dismissPurchaseConfirmation,
  showPurchaseConfirmation,
  usePurchaseConfirmation,
} from "./purchase-confirmation-store";
import { PurchaseConfirmationModal } from "./purchase-confirmation-modal";

export function PurchaseConfirmationHost() {
  const request = usePurchaseConfirmation();

  // Replay a confirmation handed off across a full-page reload (in-app charge).
  // Consumed once, so a manual refresh never re-shows it.
  useEffect(() => {
    const queued = consumeQueuedPurchaseConfirmation();
    if (queued) showPurchaseConfirmation(queued);
  }, []);

  if (!request) return null;
  return (
    <PurchaseConfirmationModal
      // Re-mount per confirmation id so the entrance animation + "shown"
      // analytics re-fire if a second purchase completes in the same session.
      key={request.id}
      request={request}
      onDismiss={dismissPurchaseConfirmation}
    />
  );
}
